import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import PlantillaScreen from '../../../helpers/PlantillaScreen';
import request from '../../../../Helpers/API';
import Swal from 'sweetalert2';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SendIcon from '@mui/icons-material/Send';
import { ButtonBack } from '../Util/ButtonBack';
import { SearchSelect } from '../../ConfigurarLeads/SearchSelect';
import { SimpleSelect } from '../../ConfigurarLeads/SimpleSelect';
import { GetASD, GroupByProps, GroupByForeignKey } from '../../../Finanzas/Actions/GetContainer';
import { capitalizePalabras } from '../../../../Helpers/CapitalizePalabras';

// Rol con el que un usuario de soporte puede levantar una incidencia a
// nombre de otra sucursal/departamento. El resto de los roles (usuario
// normal) solo captura lo básico del ticket.
const ROL_ADMIN_SOPORTE = "ADTI";

const getPrioridadInfo = (prioridad) => {
    switch (prioridad) {
        case "A":
            return { label: "Prioridad alta", clase: "nsi-badge--alta" };
        case "M":
            return { label: "Prioridad mediana", clase: "nsi-badge--media" };
        case "B":
            return { label: "Prioridad baja", clase: "nsi-badge--baja" };
        default:
            return null;
    }
};

// Formulario de alta de una nueva incidencia/actividad — versión rediseñada,
// enfocada en ser simple para el usuario normal (Sala > Grupo > Actividad >
// Comentario) y en mostrar los campos adicionales de Sucursal/Departamento
// únicamente cuando quien la levanta tiene el rol de administrador de
// soporte (ADTI), ya que en ese caso el ticket puede levantarse a nombre de
// otra sucursal.
//
// También sirve como pantalla de EDICIÓN: cuando la ruta trae un :id
// (/Permisos/SeguimientoActividadesNueva/:id), carga el ticket existente y
// precarga sala/grupo/actividad/comentarios, y al guardar llama a
// "SeguimientoAct/Editar" en vez de "SeguimientoAct/Guardar". Reemplaza así
// al botón "Editar" del listado, que antes mandaba al formulario viejo
// (SeguimientoActividadesAgregarScreen.js, que sigue existiendo pero ya no
// se enruta desde ahí). Sucursal/Departamento y los campos extra por sala no
// se editan aquí: el backend actual (SP_Cat_SeguimientoAct_Editar) todavía
// no los persiste, así que en modo edición esos bloques ni siquiera se
// muestran para no dar a entender que un cambio ahí se va a guardar.
export const SeguimientoActividadesNuevaScreen = () => {

    const history = useHistory();
    const { id } = useParams();
    const esEdicion = !!id;
    const { roles } = useSelector((state) => state.sesion);

    // TEMPORAL — para verificar en consola qué roles trae el usuario logueado
    // y confirmar el código exacto del rol de administrador de soporte.
    // eslint-disable-next-line no-console
    console.log("roles del usuario (state.sesion.roles):", roles);

    const esAdminSoporte = useMemo(
        () => (roles || []).some((r) => (typeof r === "string" ? r : r?.idrol) === ROL_ADMIN_SOPORTE),
        [roles]
    );

    const [salas, setSalas] = useState([]);
    const [salaSeleccionada, setSalaSeleccionada] = useState(null);

    // Campos extra configurables por sala (Cat_Sala.camposExtra) — se
    // cargan al elegir la sala y se pintan dinámicamente, sin necesidad de
    // tocar código para cada sala nueva que los requiera.
    const [camposExtraDefinicion, setCamposExtraDefinicion] = useState([]);
    const [camposExtraValores, setCamposExtraValores] = useState({});
    const [cargandoCamposExtra, setCargandoCamposExtra] = useState(false);

    const [actividadesSala, setActividadesSala] = useState([]);
    const [grupos, setGrupos] = useState([]);
    const [grupoSeleccionado, setGrupoSeleccionado] = useState(null);

    const [actividadesGrupo, setActividadesGrupo] = useState([]);
    const [actividadSeleccionada, setActividadSeleccionada] = useState(null);

    const [comentarios, setComentarios] = useState("");

    // Solo se cargan/usan cuando esAdminSoporte === true.
    const [asd, setAsd] = useState([]);
    const [sucursalSeleccionada, setSucursalSeleccionada] = useState(null);
    const [departamentoSeleccionado, setDepartamentoSeleccionado] = useState(null);

    const [archivo, setArchivo] = useState(null);
    const [archivoBase64, setArchivoBase64] = useState("");
    // Evidencia que ya tenía el ticket antes de entrar a editarlo. Se manda
    // como respaldo si el usuario no adjunta un archivo nuevo, para no
    // borrarla por accidente.
    const [evidenciaActual, setEvidenciaActual] = useState("");

    const [loading, setLoading] = useState(false);
    const [cargandoInicial, setCargandoInicial] = useState(true);

    // Trae actividades + grupos de una sala sin tocar ninguna otra selección
    // (a diferencia de onSeleccionarSala, que sí resetea todo lo demás por
    // ser la que dispara el usuario al cambiar de sala manualmente). Se
    // reutiliza tanto ahí como al precargar un ticket en modo edición.
    const cargarDatosSala = async (idsala) => {
        if (!idsala) {
            setActividadesSala([]);
            setGrupos([]);
            return [];
        }

        try {
            const res = await request.get(`Actividad/ActividadesPorSala/${idsala}`);
            const actividades = res?.data ?? res ?? [];
            setActividadesSala(actividades);

            const gruposUnicos = [
                ...new Map(
                    actividades.map((a) => [a.idgrupoactividades, {
                        idgrupoactividades: a.idgrupoactividades,
                        nombregrupoactividades: a.nombregrupoactividades
                    }])
                ).values()
            ];
            setGrupos(gruposUnicos);
            return actividades;
        } catch {
            Swal.fire("ERROR", "No se pudieron cargar las actividades de la sala", "error");
            return [];
        }
    };

    const cargarCamposExtraSala = async (idsala) => {
        try {
            setCargandoCamposExtra(true);
            const resCampos = await request.get(`Sala/CamposExtra/${idsala}`);
            const data = resCampos?.data ?? resCampos ?? {};
            const crudo = data.camposextra ?? data.camposExtra;
            const definicion = crudo ? JSON.parse(crudo) : [];
            setCamposExtraDefinicion(Array.isArray(definicion) ? definicion : []);
        } catch {
            // Si la sala no tiene campos extra configurados (o el JSON viene
            // vacío/mal formado) simplemente no se muestra nada extra —
            // no debe bloquear el resto del formulario.
            setCamposExtraDefinicion([]);
        } finally {
            setCargandoCamposExtra(false);
        }
    };

    useEffect(() => {
        const init = async () => {
            try {
                const resSalas = await request.get("Sala/Listado");
                setSalas(resSalas?.data ?? resSalas ?? []);

                if (esAdminSoporte) {
                    const resAsd = await GetASD(1);
                    setAsd(resAsd?.data ?? resAsd ?? []);
                }

                if (esEdicion) {
                    const resTicket = await request.get(`SeguimientoAct/Obtener/${id}`);
                    const ticket = resTicket?.data ?? resTicket ?? {};

                    // El botón "Editar" del listado ya solo aparece cuando el
                    // ticket está en estatus "Creado", pero esta pantalla
                    // también es accesible por URL directa, así que se
                    // vuelve a validar aquí (defensa en profundidad).
                    if (ticket.estatus !== "C") {
                        Swal.fire("No disponible", "Este ticket ya no se puede editar porque su estatus ya no es \"Creado\"", "warning");
                        history.push("/Permisos/CatSeguimientoActividades");
                        return;
                    }

                    setSalaSeleccionada(ticket.idSala);
                    setComentarios(ticket.comentarios || "");
                    setEvidenciaActual(ticket.evidencia || "");

                    const actividades = await cargarDatosSala(ticket.idSala);
                    await cargarCamposExtraSala(ticket.idSala);

                    const actividadEncontrada = actividades.find(
                        (a) => Number(a.idactividad) === Number(ticket.idActividad)
                    );

                    if (actividadEncontrada) {
                        setGrupoSeleccionado(actividadEncontrada.idgrupoactividades);
                        setActividadesGrupo(
                            actividades.filter(
                                (a) => Number(a.idgrupoactividades) === Number(actividadEncontrada.idgrupoactividades)
                            )
                        );
                        setActividadSeleccionada(ticket.idActividad);
                    }
                }
            } catch (error) {
                Swal.fire("Aviso", "No se pudieron cargar los catálogos iniciales", "warning");
            } finally {
                setCargandoInicial(false);
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [esAdminSoporte, esEdicion, id]);

    const sucursalesOpciones = useMemo(
        () => GroupByProps(asd, ["idsucursal", "sucursal"]),
        [asd]
    );

    const departamentosOpciones = useMemo(() => {
        if (!sucursalSeleccionada) return [];
        return GroupByForeignKey(
            asd,
            "idsucursal",
            sucursalSeleccionada,
            ["iddepartamento", "departamento"]
        );
    }, [asd, sucursalSeleccionada]);

    const onSeleccionarSala = async (idsala) => {
        setSalaSeleccionada(idsala);
        setGrupoSeleccionado(null);
        setActividadSeleccionada(null);
        setActividadesGrupo([]);
        setCamposExtraDefinicion([]);
        setCamposExtraValores({});

        if (!idsala) {
            setActividadesSala([]);
            setGrupos([]);
            return;
        }

        await cargarDatosSala(idsala);
        await cargarCamposExtraSala(idsala);
    };

    const onCambiarValorExtra = (campo, valor) => {
        setCamposExtraValores((prev) => ({ ...prev, [campo]: valor }));
    };

    const onSeleccionarGrupo = (idgrupo) => {
        setGrupoSeleccionado(idgrupo);
        setActividadSeleccionada(null);

        if (!idgrupo) {
            setActividadesGrupo([]);
            return;
        }

        setActividadesGrupo(
            actividadesSala.filter((a) => Number(a.idgrupoactividades) === Number(idgrupo))
        );
    };

    const onSeleccionarSucursal = (idsucursal) => {
        setSucursalSeleccionada(idsucursal);
        setDepartamentoSeleccionado(null);
    };

    const convertirBase64 = (file) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => setArchivoBase64(reader.result);
        reader.onerror = () => Swal.fire("Error", "No se pudo leer el archivo", "error");
    };

    const onSeleccionarArchivo = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setArchivo(file);
        convertirBase64(file);
    };

    const onQuitarArchivo = () => {
        setArchivo(null);
        setArchivoBase64("");
    };

    const onQuitarEvidenciaActual = () => {
        setEvidenciaActual("");
    };

    const actividadObj = actividadesGrupo.find(
        (a) => Number(a.idactividad) === Number(actividadSeleccionada)
    );
    const prioridadInfo = actividadObj ? getPrioridadInfo(actividadObj.prioridad) : null;

    const onGuardar = async () => {

        if (!salaSeleccionada) {
            Swal.fire("Aviso", "Selecciona una sala", "warning");
            return;
        }
        if (!grupoSeleccionado) {
            Swal.fire("Aviso", "Selecciona un grupo", "warning");
            return;
        }
        if (!actividadSeleccionada) {
            Swal.fire("Aviso", "Selecciona una actividad", "warning");
            return;
        }
        // Sucursal/Departamento y campos extra solo se capturan (y se
        // validan) al dar de alta. En edición esos bloques ni se muestran,
        // porque el backend de edición todavía no los persiste.
        if (!esEdicion && esAdminSoporte && !sucursalSeleccionada) {
            Swal.fire("Aviso", "Selecciona la sucursal a la que pertenece esta incidencia", "warning");
            return;
        }
        if (!esEdicion && esAdminSoporte && !departamentoSeleccionado) {
            Swal.fire("Aviso", "Selecciona el departamento", "warning");
            return;
        }

        if (!esEdicion) {
            const campoFaltante = camposExtraDefinicion.find(
                (c) => c.requerido && !String(camposExtraValores[c.campo] ?? "").trim()
            );
            if (campoFaltante) {
                Swal.fire("Aviso", `Falta el campo "${campoFaltante.etiqueta}"`, "warning");
                return;
            }
        }

        try {
            setLoading(true);

            const payload = {
                idSolicitud: esEdicion ? Number(id) : 0,
                idSala: Number(salaSeleccionada),
                idActividad: Number(actividadSeleccionada),
                comentarios: comentarios || "",
                estatus: "C",
                // La prioridad la define la actividad elegida, no el usuario
                // (se muestra como referencia en el badge de arriba).
                prioridad: actividadObj?.prioridad || "M",
                evidencia: archivoBase64 || evidenciaActual || "",
                ...(!esEdicion && esAdminSoporte
                    ? {
                        idSucursal: Number(sucursalSeleccionada),
                        idDepartamento: Number(departamentoSeleccionado),
                    }
                    : {}),
                ...(!esEdicion && camposExtraDefinicion.length > 0
                    ? { datosExtra: JSON.stringify(camposExtraValores) }
                    : {}),
            };

            if (esEdicion) {
                await request.post("SeguimientoAct/Editar", payload);
                Swal.fire("Correcto", "Ticket actualizado correctamente", "success");
            } else {
                await request.post("SeguimientoAct/Guardar", payload);
                Swal.fire("Correcto", "Incidencia registrada correctamente", "success");
            }

            history.push("/Permisos/CatSeguimientoActividades");
        } catch (error) {
            Swal.fire("Error", esEdicion ? "No se pudo actualizar el ticket" : "No se pudo registrar la incidencia", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <PlantillaScreen
            titulo={esEdicion ? "EDITAR TICKET" : "AGREGAR SEGUIMIENTO DE ACTIVIDAD"}
            className_main={1}
            className={"seguimiento-actividades-contenedor"}
        >
            <div className="nsi-contenido">

                {!esEdicion && esAdminSoporte && (
                    <div className="nsi-rol-aviso">
                        <span className="nsi-rol-aviso-badge">ADMIN SOPORTE</span>
                        Estás levantando esta incidencia con permisos de administrador: puedes indicar
                        la sucursal y el departamento a nombre de quien reportas.
                    </div>
                )}

                <div className="nsi-card">
                    <div className="nsi-card-header">
                        <span className="nsi-card-header-numero">1</span>
                        <div>
                            <div className="nsi-card-header-titulo">¿Dónde ocurre?</div>
                            <div className="nsi-card-header-subtitulo">Selecciona la sala relacionada con la incidencia</div>
                        </div>
                    </div>

                    <div className="lead-form-group">
                        <label>Sala</label>
                        <SearchSelect
                            options={salas.map((s) => ({ value: s.idsala, label: s.nombresala }))}
                            value={salaSeleccionada}
                            onChange={onSeleccionarSala}
                            placeholder={cargandoInicial ? "Cargando salas..." : "Seleccione una sala"}
                        />
                    </div>

                    {!esEdicion && esAdminSoporte && (
                        <div className="nsi-admin-fields">
                            <div className="lead-form-group">
                                <label>Sucursal</label>
                                <SearchSelect
                                    options={sucursalesOpciones.map((s) => ({
                                        value: s.idsucursal,
                                        label: capitalizePalabras(s.sucursal || ""),
                                    }))}
                                    value={sucursalSeleccionada}
                                    onChange={onSeleccionarSucursal}
                                    placeholder="Seleccione una sucursal"
                                />
                            </div>
                            <div className="lead-form-group">
                                <label>Departamento</label>
                                <SearchSelect
                                    options={departamentosOpciones.map((d) => ({
                                        value: d.iddepartamento,
                                        label: capitalizePalabras(d.departamento || ""),
                                    }))}
                                    value={departamentoSeleccionado}
                                    onChange={setDepartamentoSeleccionado}
                                    placeholder={sucursalSeleccionada ? "Seleccione un departamento" : "Primero selecciona una sucursal"}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className={`nsi-card ${!salaSeleccionada ? "nsi-card--disabled" : ""}`}>
                    <div className="nsi-card-header">
                        <span className="nsi-card-header-numero">2</span>
                        <div>
                            <div className="nsi-card-header-titulo">¿Qué necesitas reportar?</div>
                            <div className="nsi-card-header-subtitulo">Elige el grupo y la actividad específica</div>
                        </div>
                    </div>

                    <div className="lead-form-group">
                        <label>Grupo</label>
                        <SearchSelect
                            options={grupos.map((g) => ({ value: g.idgrupoactividades, label: g.nombregrupoactividades }))}
                            value={grupoSeleccionado}
                            onChange={onSeleccionarGrupo}
                            placeholder={salaSeleccionada ? "Seleccione un grupo" : "Primero selecciona una sala"}
                        />
                    </div>

                    <div className="lead-form-group">
                        <label>Actividad</label>
                        <SearchSelect
                            options={actividadesGrupo.map((a) => ({ value: a.idactividad, label: a.nombreactividad }))}
                            value={actividadSeleccionada}
                            onChange={setActividadSeleccionada}
                            placeholder={grupoSeleccionado ? "Seleccione una actividad" : "Primero selecciona un grupo"}
                        />
                    </div>

                    {prioridadInfo && (
                        <span className={`nsi-badge ${prioridadInfo.clase}`}>{prioridadInfo.label}</span>
                    )}
                </div>

                {!esEdicion && camposExtraDefinicion.length > 0 && (
                    <div className="nsi-card">
                        <div className="nsi-card-header">
                            <span className="nsi-card-header-numero">3</span>
                            <div>
                                <div className="nsi-card-header-titulo">Información adicional</div>
                                <div className="nsi-card-header-subtitulo">
                                    {cargandoCamposExtra ? "Cargando..." : "Esta sala pide algunos datos extra"}
                                </div>
                            </div>
                        </div>

                        {camposExtraDefinicion.map((c) => (
                            <div className="lead-form-group" key={c.campo}>
                                <label>
                                    {c.etiqueta}{c.requerido ? " *" : ""}
                                </label>
                                {c.tipo === "lista" ? (
                                    <SimpleSelect
                                        options={(c.opciones || []).map((op) => ({ value: op, label: op }))}
                                        value={camposExtraValores[c.campo] || ""}
                                        onChange={(val) => onCambiarValorExtra(c.campo, val)}
                                        placeholder={`Seleccione ${c.etiqueta.toLowerCase()}`}
                                    />
                                ) : (
                                    <input
                                        type={c.tipo === "numero" ? "number" : c.tipo === "fecha" ? "date" : "text"}
                                        value={camposExtraValores[c.campo] || ""}
                                        onChange={(e) => onCambiarValorExtra(c.campo, e.target.value)}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div className="nsi-card">
                    <div className="nsi-card-header">
                        <span className="nsi-card-header-numero">{!esEdicion && camposExtraDefinicion.length > 0 ? 4 : 3}</span>
                        <div>
                            <div className="nsi-card-header-titulo">Cuéntanos más</div>
                            <div className="nsi-card-header-subtitulo">Un comentario breve ayuda a resolverlo más rápido (opcional)</div>
                        </div>
                    </div>

                    <div className="lead-form-group">
                        <label>Comentarios</label>
                        <textarea
                            rows={4}
                            value={comentarios}
                            onChange={(e) => setComentarios(e.target.value)}
                            placeholder="Describe brevemente lo que sucede..."
                        />
                    </div>

                    <div className="nsi-dropzone">
                        {archivo ? (
                            <div className="nsi-dropzone-archivo">
                                <InsertDriveFileIcon />
                                <span className="nsi-dropzone-archivo-nombre">{archivo.name}</span>
                                <DeleteOutlineIcon className="nsi-dropzone-archivo-quitar" onClick={onQuitarArchivo} />
                            </div>
                        ) : evidenciaActual ? (
                            <div className="nsi-dropzone-archivo">
                                <InsertDriveFileIcon />
                                <span className="nsi-dropzone-archivo-nombre">Evidencia ya adjuntada</span>
                                <DeleteOutlineIcon className="nsi-dropzone-archivo-quitar" onClick={onQuitarEvidenciaActual} />
                            </div>
                        ) : (
                            <label className="nsi-dropzone-vacio">
                                <UploadFileIcon />
                                <span>Adjuntar evidencia (opcional)</span>
                                <input type="file" hidden onChange={onSeleccionarArchivo} />
                            </label>
                        )}
                    </div>
                </div>

                <div className="nsi-footer">
                    <button className="nsi-footer-cancelar" onClick={() => history.push("/Permisos/CatSeguimientoActividades")}>
                        Cancelar
                    </button>
                    <button className="nsi-footer-guardar" onClick={onGuardar} disabled={loading}>
                        {loading
                            ? (esEdicion ? "Actualizando..." : "Guardando...")
                            : (esEdicion ? "Actualizar ticket" : "Guardar incidencia")}
                    </button>
                </div>

            </div>

            <ButtonBack path={"/Permisos/CatSeguimientoActividades"} />

            {loading && (
                <div className="nsi-loading-overlay">
                    <div className="nsi-loading-card">
                        <div className="nsi-loading-spinner">
                            <span></span>
                            <span></span>
                            <SendIcon />
                        </div>
                        <div className="nsi-loading-titulo">
                            {esEdicion ? "Actualizando tu ticket" : "Guardando tu incidencia"}
                        </div>
                        <div className="nsi-loading-subtitulo">
                            Estamos registrando todo, un momento
                            <span className="nsi-loading-dots"><i></i><i></i><i></i></span>
                        </div>
                    </div>
                </div>
            )}

        </PlantillaScreen>
    );
};

export default SeguimientoActividadesNuevaScreen;
